import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useInstallPrompt } from '@/lib/pwa';
import { useAuth } from '@/contexts/AuthContext';

const DISMISSED_KEY = 'pwa-install-dismissed-v1';

/** Dashboard-only install banner. Hidden once dismissed or installed. */
export function InstallAppPrompt() {
  const { user } = useAuth();
  const { canInstall, installed, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && window.localStorage?.getItem(DISMISSED_KEY) === '1'
  );
  const [promptResult, setPromptResult] = useState(null);

  useEffect(() => {
    if (installed) setDismissed(true);
  }, [installed]);

  if (!user || installed || dismissed || !canInstall || promptResult === 'accepted') return null;

  const handleInstall = async () => {
    const { outcome } = await promptInstall();
    setPromptResult(outcome);
    if (outcome === 'dismissed') handleDismiss();
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage?.setItem(DISMISSED_KEY, '1'); } catch (e) { /* ignore */ }
  };

  return (
    <div
      className="mb-5 flex items-center gap-3 bg-gradient-to-r from-[#EFF6FF] to-white border border-[#0047AB]/20 rounded-sm p-3 sm:p-4"
      data-testid="install-app-banner"
    >
      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0047AB] rounded-sm flex items-center justify-center shrink-0">
        <Download className="w-4.5 h-4.5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 leading-tight">Install Real Leads</p>
        <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-snug">
          Add to your home screen for faster access, offline dashboard, and push alerts.
        </p>
      </div>
      <button
        onClick={handleInstall}
        className="shrink-0 bg-[#0047AB] hover:bg-[#003580] text-white text-xs font-semibold rounded-sm px-3 py-1.5"
        data-testid="install-app-btn"
      >
        Install
      </button>
      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 text-slate-400 hover:text-slate-600 rounded-sm"
        aria-label="Dismiss install prompt"
        data-testid="install-app-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
